import {
  ActionIcon,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconCpu,
  IconDatabase,
  IconRefresh,
  IconServer,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Fragment, type ReactNode, useState } from 'react';

import { apiHook } from '../treaty';

type LogLevel = 'all' | 'debug' | 'info' | 'warn' | 'error';

const levelColors = {
  debug: 'gray',
  info: 'blue',
  warn: 'yellow',
  error: 'red',
} as const;

function formatCapacity(bytes: number | undefined): string {
  if (bytes === undefined) return '—';
  const gibibytes = bytes / 1024 ** 3;
  return `${gibibytes.toFixed(gibibytes >= 100 ? 0 : 1)} GiB`;
}

function metricColor(percent: number | undefined): 'green' | 'yellow' | 'red' | 'gray' {
  if (percent === undefined) return 'gray';
  if (percent >= 85) return 'red';
  if (percent >= 70) return 'yellow';
  return 'green';
}

function MetricCard({
  title,
  icon,
  percent,
  detail,
}: {
  title: string;
  icon: ReactNode;
  percent?: number;
  detail: string;
}) {
  const color = metricColor(percent);
  return (
    <Paper withBorder radius='lg' p='md'>
      <Stack gap='sm'>
        <Group justify='space-between' wrap='nowrap'>
          <Group gap='sm' wrap='nowrap'>
            <ThemeIcon variant='light' color={color} size='lg' radius='md'>
              {icon}
            </ThemeIcon>
            <Text fw={700}>{title}</Text>
          </Group>
          <Badge variant='light' color={color} size='lg'>
            {percent === undefined ? '—' : `${percent.toFixed(1)}%`}
          </Badge>
        </Group>
        <Progress value={percent ?? 0} color={color} size='sm' radius='xl' />
        <Text size='xs' c='dimmed'>
          {detail}
        </Text>
      </Stack>
    </Paper>
  );
}

export function LogsPage() {
  const [level, setLevel] = useState<LogLevel>('all');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState('100');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300);
  const selectedLevel = level === 'all' ? undefined : level;
  const logsQuery = apiHook.useQuery(
    (client) =>
      client.admin.logs.get({
        query: {
          limit: Number(limit),
          ...(selectedLevel ? { level: selectedLevel } : {}),
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      }),
    { refetchInterval: 5_000 },
  );
  const metricsQuery = apiHook.useQuery((client) => client.admin['server-metrics'].get(), {
    refetchInterval: 5_000,
  });
  const metrics = metricsQuery.data;

  return (
    <Stack gap='lg' maw={1200} mx='auto'>
      <Group justify='space-between' align='end'>
        <div>
          <Title order={1}>Server logs</Title>
          <Text c='dimmed' mt={5}>
            Inspect structured backend events without opening the server console.
          </Text>
        </div>
        <Group gap='xs'>
          <Badge variant='light' color={metricsQuery.isError ? 'red' : 'green'} size='lg'>
            {metricsQuery.isError ? 'Metrics unavailable' : 'Updates every 5s'}
          </Badge>
          <Badge variant='light' size='lg'>
            {logsQuery.data?.logs.length ?? 0} entries
          </Badge>
          <ActionIcon
            variant='default'
            size='lg'
            aria-label='Refresh logs'
            loading={logsQuery.isFetching}
            onClick={() => void logsQuery.refetch()}
          >
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='md'>
        <MetricCard
          title='CPU busy'
          icon={<IconCpu size={20} />}
          percent={metrics?.cpu.busyPercent}
          detail='System-wide active CPU time; idle and I/O-wait time are excluded.'
        />
        <MetricCard
          title='RAM used'
          icon={<IconServer size={20} />}
          percent={metrics?.memory.usedPercent}
          detail={
            metrics
              ? `${formatCapacity(metrics.memory.usedBytes)} used · ${formatCapacity(metrics.memory.availableBytes)} available`
              : 'Waiting for the server memory snapshot…'
          }
        />
        <MetricCard
          title='Disk used'
          icon={<IconDatabase size={20} />}
          percent={metrics?.disk.usedPercent}
          detail={
            metrics
              ? `${formatCapacity(metrics.disk.availableBytes)} free of ${formatCapacity(metrics.disk.totalBytes)}`
              : 'Waiting for the storage filesystem snapshot…'
          }
        />
      </SimpleGrid>

      <Paper withBorder radius='lg' p='md'>
        <Group align='end'>
          <TextInput
            label='Search'
            placeholder='Message, module, file ID, error…'
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            label='Level'
            value={level}
            data={[
              { value: 'all', label: 'All levels' },
              { value: 'debug', label: 'Debug' },
              { value: 'info', label: 'Info' },
              { value: 'warn', label: 'Warning' },
              { value: 'error', label: 'Error' },
            ]}
            onChange={(value) => setLevel((value as LogLevel | null) ?? 'all')}
            w={150}
          />
          <Select
            label='Rows'
            value={limit}
            data={['50', '100', '200']}
            onChange={(value) => value && setLimit(value)}
            w={100}
          />
        </Group>
      </Paper>

      <Paper withBorder radius='lg' className='data-card'>
        <Table.ScrollContainer minWidth={820}>
          <Table verticalSpacing='sm' horizontalSpacing='lg' highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={42} />
                <Table.Th w={170}>Time</Table.Th>
                <Table.Th w={95}>Level</Table.Th>
                <Table.Th w={140}>Module</Table.Th>
                <Table.Th>Message</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {logsQuery.data?.logs.map((log) => {
                const expanded = expandedId === log.id;
                const contextText = JSON.stringify(log.context, null, 2);
                return (
                  <Fragment key={log.id}>
                    <Table.Tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedId(expanded ? null : log.id)}
                    >
                      <Table.Td>
                        {expanded ? <IconChevronDown size={17} /> : <IconChevronRight size={17} />}
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>{dayjs(log.createdAt).format('DD MMM, HH:mm:ss')}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={levelColors[log.level]} variant='light'>
                          {log.level}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm' c='dimmed'>
                          {log.module ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm' fw={600} lineClamp={2}>
                          {log.message}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                    {expanded && (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Box
                            bg='var(--mantine-color-gray-0)'
                            p='md'
                            style={{ borderRadius: 'var(--mantine-radius-md)' }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Group justify='space-between' mb='xs'>
                              <Text size='xs' fw={700} c='dimmed' tt='uppercase'>
                                Structured context
                              </Text>
                              <CopyButton value={contextText} timeout={1500}>
                                {({ copied, copy }) => (
                                  <Tooltip label={copied ? 'Copied' : 'Copy JSON'}>
                                    <Button
                                      size='compact-xs'
                                      variant='subtle'
                                      leftSection={
                                        copied ? <IconCheck size={14} /> : <IconCopy size={14} />
                                      }
                                      onClick={copy}
                                    >
                                      {copied ? 'Copied' : 'Copy'}
                                    </Button>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            </Group>
                            <Box
                              component='pre'
                              m={0}
                              style={{
                                overflowX: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: 12,
                              }}
                            >
                              {contextText}
                            </Box>
                          </Box>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                );
              })}
              {!logsQuery.isLoading && logsQuery.data?.logs.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c='dimmed' ta='center' py='xl'>
                      No matching logs.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
